/**
 * Structured logger — v.1-D §7.2.
 *
 * Emits one JSON record per log line so the browser console (and any tail
 * harness) sees a parsable envelope: `{ ts, level, message, ...context }`.
 * The minimum level is read from `env.LOG_LEVEL` at module load — lower-
 * priority calls are silently dropped.
 *
 * Console-method routing:
 *   error → console.error
 *   warn  → console.warn
 *   debug → console.debug
 *   info  → console.log    (info is the default visible level)
 */
import { env } from './env';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

const LEVEL_ORDER: Record<LogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
};

const minLevel =
  LEVEL_ORDER[
    (env.LOG_LEVEL as LogLevel) in LEVEL_ORDER
      ? (env.LOG_LEVEL as LogLevel)
      : 'info'
  ];

interface LogContext {
  [key: string]: unknown;
}

function emit(level: LogLevel, message: string, context?: LogContext): void {
  if (LEVEL_ORDER[level] < minLevel) return;
  const record = {
    ts: new Date().toISOString(),
    level,
    message,
    ...context,
  };
  const fn =
    level === 'error'
      ? console.error
      : level === 'warn'
        ? console.warn
        : level === 'debug'
          ? console.debug
          : console.log;
  fn(JSON.stringify(record));
}

export const logger = {
  debug: (m: string, c?: LogContext) => emit('debug', m, c),
  info: (m: string, c?: LogContext) => emit('info', m, c),
  warn: (m: string, c?: LogContext) => emit('warn', m, c),
  error: (m: string, c?: LogContext) => emit('error', m, c),
};
